import React, { useEffect, useMemo, useState } from 'react';
import { RevenueSummary } from '../types';
import invoiceService from '../services/invoiceService';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Umsatz eines Jahres aus den gestellten Rechnungen, dazu die Hochrechnung
 * aus den laufenden Serienrechnungen.
 *
 * Gezeigt wird der Nettobetrag: die Umsatzsteuer ist durchlaufender Posten und
 * kein Ertrag. Entwürfe zählen nicht mit, stornierte Rechnungen und die
 * zugehörigen Stornorechnungen fallen beide heraus.
 *
 * Die Prognose ist bewusst keine dritte Farbe, sondern ein Umriss in der Farbe
 * der offenen Posten: gleiche Bedeutung, nur noch nicht gestellt. Das bleibt bei
 * Farbfehlsichtigkeit und in Graustufen unterscheidbar, weil die Form trägt.
 */

/* Zwei Reihen, zwei Farben - je Modus eigene Stufen, gegen die jeweilige
   Flächenfarbe geprüft (Helligkeitsband, Chroma, Farbfehlsichtigkeit, Kontrast). */
const SERIES = {
  light: { paid: '#4f46e5', open: '#0891b2' },
  dark: { paid: '#6366f1', open: '#0e9fbf' },
};

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const euro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    .format(cents / 100);

const euroExact = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const RevenueCard: React.FC = () => {
  const [data, setData] = useState<RevenueSummary | null>(null);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const { theme } = useTheme();
  const colors = theme === 'dark' ? SERIES.dark : SERIES.light;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    invoiceService.revenue(year)
      .then(r => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  // Der Maßstab muss die Prognose einschließen, sonst liefe der Balken über.
  const max = useMemo(
    () => Math.max(1, ...(data?.months ?? []).map(m => m.netCents + m.forecastNetCents)),
    [data],
  );

  if (loading && !data) {
    return (
      <div className="rounded-xl p-5 mb-6"
        style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
        <div className="skeleton h-3 w-32 mb-4"></div>
        <div className="skeleton h-8 w-48"></div>
      </div>
    );
  }
  if (!data) return null;

  const hasForecast = data.forecastNetCents > 0;
  const hasRevenue = data.netCents !== 0 || data.draftNetCents !== 0 || hasForecast;

  return (
    <div className="rounded-xl p-5 mb-6 animate-fade-up"
      style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>

      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Umsatz {data.year} <span className="normal-case font-medium">(netto)</span>
        </h2>
        {data.availableYears.length > 1 && (
          <div className="flex gap-1">
            {data.availableYears.map(y => (
              <button key={y} onClick={() => setYear(y)}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold border"
                style={{
                  borderColor: y === data.year ? 'transparent' : 'var(--surface-border)',
                  background: y === data.year ? colors.paid : 'transparent',
                  color: y === data.year ? '#ffffff' : 'var(--text-muted)',
                }}>
                {y}
              </button>
            ))}
          </div>
        )}
      </div>

      {!hasRevenue ? (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Für {data.year} sind noch keine Rechnungen gestellt.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4 mb-4">
            <div>
              <div className="text-3xl font-extrabold tracking-tight" style={{ color: 'var(--text-primary)' }}>
                {euroExact(data.netCents)}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                gestellt{' · '}aus {data.invoiceCount} {data.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'}
                {' · '}{euroExact(data.grossCents)} brutto
              </div>
            </div>
            {hasForecast && (
              <div>
                <div className="text-3xl font-extrabold tracking-tight" style={{ color: colors.open }}>
                  {euroExact(data.expectedNetCents)}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  erwartet {data.year}{' · '}inkl. {euroExact(data.forecastNetCents)} aus{' '}
                  {data.forecastInvoiceCount} noch fälligen {data.forecastInvoiceCount === 1 ? 'Serienrechnung' : 'Serienrechnungen'}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-x-8 gap-y-3 nums-tabular mb-5">
            <Figure label="bezahlt" value={euroExact(data.paidNetCents)} swatch={colors.paid} />
            <Figure label="offen" value={euroExact(data.openNetCents)} swatch={colors.open}
              note={data.overdueNetCents > 0 ? `davon ${euroExact(data.overdueNetCents)} überfällig` : undefined} />
            {hasForecast && (
              <Figure label="noch zu stellen" value={euroExact(data.forecastNetCents)} outlineSwatch={colors.open} />
            )}
            {data.draftNetCents !== 0 && (
              <Figure label="in Entwürfen" value={euroExact(data.draftNetCents)} />
            )}
          </div>

          {/* Monatsbalken: bezahlt unten, offen darüber, die Prognose als Umriss obenauf */}
          <div className="relative">
            <div className="flex items-end gap-1.5" style={{ height: '120px' }}>
              {data.months.map(m => {
                const paidH = (m.paidNetCents / max) * 100;
                const openH = (Math.max(0, m.netCents - m.paidNetCents) / max) * 100;
                const forecastH = (m.forecastNetCents / max) * 100;
                const topmost = forecastH > 0 ? 'forecast' : openH > 0 ? 'open' : 'paid';
                const round = (part: string) => (topmost === part ? '4px 4px 0 0' : '0');
                return (
                  <div key={m.month}
                    className="flex-1 flex flex-col justify-end h-full cursor-default"
                    onMouseEnter={() => setHovered(m.month)}
                    onMouseLeave={() => setHovered(null)}>
                    <div className="flex flex-col justify-end h-full"
                      style={{ opacity: hovered === null || hovered === m.month ? 1 : 0.55 }}>
                      {forecastH > 0 && (
                        <div style={{
                          height: `${forecastH}%`, minHeight: '6px',
                          border: `1.5px dashed ${colors.open}`,
                          borderRadius: round('forecast'),
                          marginBottom: paidH > 0 || openH > 0 ? '2px' : 0,
                        }} />
                      )}
                      {openH > 0 && (
                        <div style={{
                          height: `${openH}%`, background: colors.open,
                          borderRadius: round('open'),
                          marginBottom: paidH > 0 ? '2px' : 0,
                        }} />
                      )}
                      {paidH > 0 && (
                        <div style={{ height: `${paidH}%`, background: colors.paid, borderRadius: round('paid') }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-1.5 mt-1.5">
              {data.months.map(m => (
                <div key={m.month} className="flex-1 text-center text-[10px]"
                  style={{ color: hovered === m.month ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {MONTHS[m.month - 1]}
                </div>
              ))}
            </div>

            {hovered !== null && (
              <div className="mt-3 text-xs nums-tabular" style={{ color: 'var(--text-muted)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{MONTHS[hovered - 1]} {data.year}</strong>
                {' · '}gesamt {euro(data.months[hovered - 1].netCents)}
                {' · '}bezahlt {euro(data.months[hovered - 1].paidNetCents)}
                {' · '}offen {euro(data.months[hovered - 1].netCents - data.months[hovered - 1].paidNetCents)}
                {data.months[hovered - 1].forecastNetCents > 0 &&
                  `${' · '}noch zu stellen ${euro(data.months[hovered - 1].forecastNetCents)}`}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
            <LegendChip color={colors.paid} label="bezahlt" />
            <LegendChip color={colors.open} label="offen" />
            {hasForecast && <LegendChip color={colors.open} label="noch zu stellen" outline />}
            <button onClick={() => setShowTable(v => !v)} className="underline">
              {showTable ? 'Tabelle ausblenden' : 'Als Tabelle'}
            </button>
          </div>

          {showTable && (
            <table className="w-full text-xs mt-3 nums-tabular">
              <thead style={{ color: 'var(--text-muted)' }}>
                <tr className="text-left">
                  <th className="py-1">Monat</th>
                  <th className="py-1 text-right">Gesamt</th>
                  <th className="py-1 text-right">Bezahlt</th>
                  <th className="py-1 text-right">Offen</th>
                  {hasForecast && <th className="py-1 text-right">Noch zu stellen</th>}
                </tr>
              </thead>
              <tbody>
                {data.months.filter(m => m.netCents !== 0 || m.forecastNetCents !== 0).map(m => (
                  <tr key={m.month} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                    <td className="py-1">{MONTHS[m.month - 1]}</td>
                    <td className="py-1 text-right">{euroExact(m.netCents)}</td>
                    <td className="py-1 text-right">{euroExact(m.paidNetCents)}</td>
                    <td className="py-1 text-right">{euroExact(m.netCents - m.paidNetCents)}</td>
                    {hasForecast && (
                      <td className="py-1 text-right" style={{ color: 'var(--text-muted)' }}>
                        {m.forecastNetCents !== 0 ? euroExact(m.forecastNetCents) : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {showTable && hasForecast && (
            <table className="w-full text-xs mt-4 nums-tabular">
              <thead style={{ color: 'var(--text-muted)' }}>
                <tr className="text-left">
                  <th className="py-1">Serie, die {data.year} noch fällig wird</th>
                  <th className="py-1 text-right">Rechnungen</th>
                  <th className="py-1 text-right">je Lauf</th>
                  <th className="py-1 text-right">Summe</th>
                </tr>
              </thead>
              <tbody>
                {data.forecastSeries.map(f => (
                  <tr key={f.id} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                    <td className="py-1">
                      {f.name}
                      <span style={{ color: 'var(--text-muted)' }}> · {f.clientName}</span>
                    </td>
                    <td className="py-1 text-right">{f.invoiceCount}×</td>
                    <td className="py-1 text-right">{euroExact(f.netCentsPerRun)}</td>
                    <td className="py-1 text-right">{euroExact(f.netCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const Figure: React.FC<{ label: string; value: string; swatch?: string; outlineSwatch?: string; note?: string }> =
  ({ label, value, swatch, outlineSwatch, note }) => (
    <div>
      <div className="flex items-center gap-1.5">
        {swatch && <span style={{
          width: 8, height: 8, borderRadius: 2, background: swatch, display: 'inline-block',
        }} />}
        {outlineSwatch && <span style={{
          width: 8, height: 8, borderRadius: 2, border: `1.5px dashed ${outlineSwatch}`, display: 'inline-block',
        }} />}
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <div className="text-lg font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{value}</div>
      {note && <div className="text-[11px]" style={{ color: 'var(--badge-red-text)' }}>{note}</div>}
    </div>
  );

const LegendChip: React.FC<{ color: string; label: string; outline?: boolean }> = ({ color, label, outline }) => (
  <span className="flex items-center gap-1.5">
    <span style={{
      width: 10, height: 10, borderRadius: 2, display: 'inline-block',
      background: outline ? 'transparent' : color,
      border: outline ? `1.5px dashed ${color}` : undefined,
    }} />
    {label}
  </span>
);

export default RevenueCard;
